import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { ChangeEventHandler, FC, ReactNode, useCallback, useState } from 'react';
import { createContext } from './utils';
import styles from './work.module.scss';

type CheckboxGroupProps = {
  children: ReactNode;
  value: string[];
  onChange: (value: string[]) => void;
};

const [ctx, useCheckboxGroup] = createContext<
  | {
      value: string[];
      onChange: ChangeEventHandler<HTMLInputElement>;
    }
  | undefined
>();

export const CheckboxGroup: FC<CheckboxGroupProps> = (props) => {
  const { value, children, onChange: onChangeProp } = props;

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (evt) => {
      const v = evt.target.value;
      const newValue = [...value];
      const idx = newValue.findIndex((e) => e === v);
      if (evt.target.checked && idx < 0) {
        newValue.push(v);
      } else if (!evt.target.checked && idx >= 0) {
        newValue.splice(idx, 1);
      }
      onChangeProp(newValue);
    },
    [value, onChangeProp],
  );

  return (
    <ctx.Provider
      value={{
        value,
        onChange,
      }}
    >
      {children}
    </ctx.Provider>
  );
};

type CheckboxProps = {
  children: ReactNode;
  value: string;
  // eslint-disable-next-line react/require-default-props
  onChange?: ChangeEventHandler<HTMLInputElement>;
  // eslint-disable-next-line react/require-default-props
  checked?: boolean;
};

export const Checkbox: FC<CheckboxProps> = (props) => {
  const { children, checked: checkedProp, value, onChange: onChangeProp } = props;

  const [focus, setFocus] = useState(false);

  const group = useCheckboxGroup();

  const onChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (evt) => {
      onChangeProp?.(evt);
      group?.onChange(evt);
    },
    [onChangeProp, group],
  );

  let checked = !!checkedProp;
  if (group?.value) {
    checked = group.value.includes(value);
  }

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={styles.checkbox} data-checked={checked} data-focus={focus}>
      <input
        type='checkbox'
        className={styles.checkboxInput}
        value={value}
        onChange={onChange}
        checked={checked}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      />
      <span className={styles.checkboxIcon}>
        <FontAwesomeIcon icon={faCheck} />
      </span>
      <span className={styles.checkboxLabel}>{children}</span>
    </label>
  );
};
